import { useState, useEffect, useRef } from "react";
import { Link } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { PublicLayout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardFooter } from "@/components/ui/card";
import { Loader2, CheckCircle2, AlertCircle, Clock, X } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { ApiError, ResponseParseError } from "@workspace/api-client-react";

const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export default function Login() {
  const { login } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [bannerFading, setBannerFading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [rateLimitMinutes, setRateLimitMinutes] = useState<number | null>(null);
  const bannerTimers = useRef<{ fade: ReturnType<typeof setTimeout>; remove: ReturnType<typeof setTimeout> } | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reset") === "success") {
      setPasswordResetSuccess(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (!passwordResetSuccess) return;
    setBannerFading(false);
    const fade = setTimeout(() => setBannerFading(true), 4500);
    const remove = setTimeout(() => setPasswordResetSuccess(false), 5000);
    bannerTimers.current = { fade, remove };
    return () => {
      clearTimeout(fade);
      clearTimeout(remove);
    };
  }, [passwordResetSuccess]);

  function dismissBanner() {
    if (bannerTimers.current) {
      clearTimeout(bannerTimers.current.fade);
      clearTimeout(bannerTimers.current.remove);
      bannerTimers.current = null;
    }
    setPasswordResetSuccess(false);
    setBannerFading(false);
  }

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: "", password: "" },
  });

  async function onSubmit(values: z.infer<typeof loginSchema>) {
    try {
      setIsSubmitting(true);
      setLoginError(null);
      setRateLimitMinutes(null);
      await login(values);
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 429) {
        const secs = (error.data as { retryAfterSecs?: number } | null)?.retryAfterSecs;
        const retryHeader = error.headers.get("Retry-After");
        const seconds = secs ?? (retryHeader ? parseInt(retryHeader, 10) : null) ?? 900;
        setRateLimitMinutes(Math.ceil(seconds / 60));
      } else if (
        error instanceof ResponseParseError ||
        (error instanceof ApiError && (error.status === 502 || error.status === 503 || error.status === 404))
      ) {
        setLoginError("Service temporarily unavailable. Please try again in a moment.");
      } else {
        const msg = error instanceof Error ? error.message : "Invalid email or password";
        setLoginError(msg || "Invalid email or password");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <PublicLayout>
      <SEOHead
        title="Log In — Access Your Gym Dashboard"
        description="Log in to Open Mat to manage your gym, view trainer schedules, book private lessons, or check your session history."
        canonicalPath="/login"
        noindex
      />
      <div className="flex-1 flex items-center justify-center p-4 relative">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 blur-[120px] rounded-full pointer-events-none" />
        
        <Card className="w-full max-w-md glass-panel border-white/10 z-10 relative overflow-hidden">
          <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent" />
          <CardHeader className="space-y-1 pb-8 text-center">
            <h1 className="text-3xl font-display font-semibold uppercase tracking-wider text-white leading-none">Welcome Back</h1>
            <CardDescription className="text-muted-foreground">Enter your credentials to access your account</CardDescription>
          </CardHeader>
          <CardContent>
            {passwordResetSuccess && (
              <div className={`mb-6 flex items-center gap-3 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 transition-opacity duration-500 ${bannerFading ? "opacity-0" : "opacity-100"}`}>
                <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                <p className="text-sm text-green-300 flex-1">Your password has been updated. Please sign in.</p>
                <button type="button" onClick={dismissBanner} aria-label="Dismiss" className="text-green-400/70 hover:text-green-300 transition-colors shrink-0 self-start">
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <Label className="text-white/80">Email</Label>
                      <FormControl>
                        <Input 
                          placeholder="name@example.com" 
                          className="bg-black/50 border-white/10 focus-visible:ring-primary focus-visible:border-primary text-white h-12 rounded-xl"
                          {...field}
                          onChange={(e) => { field.onChange(e); setLoginError(null); }}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <Label className="text-white/80">Password</Label>
                        <Link href="/forgot-password"><span className="text-xs text-primary hover:underline cursor-pointer">Forgot password?</span></Link>
                      </div>
                      <FormControl>
                        <Input 
                          type="password" 
                          placeholder="••••••••" 
                          className="bg-black/50 border-white/10 focus-visible:ring-primary focus-visible:border-primary text-white h-12 rounded-xl"
                          {...field}
                          onChange={(e) => { field.onChange(e); setLoginError(null); }}
                        />
                      </FormControl>
                      <FormMessage className="text-destructive" />
                    </FormItem>
                  )}
                />
                {rateLimitMinutes !== null && (
                  <div className="rounded-xl border border-orange-500/30 bg-orange-500/10 px-4 py-3 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-orange-400 shrink-0" />
                      <p className="text-sm font-semibold text-orange-300">Account temporarily locked</p>
                    </div>
                    <p className="text-xs text-orange-200/80 leading-relaxed pl-6">
                      Too many failed attempts. Please wait{" "}
                      <span className="text-white font-medium">{rateLimitMinutes} minute{rateLimitMinutes !== 1 ? "s" : ""}</span>{" "}
                      before trying again, or{" "}
                      <Link href="/forgot-password">
                        <span className="text-primary hover:underline cursor-pointer font-medium">reset your password</span>
                      </Link>
                      .
                    </p>
                  </div>
                )}
                {loginError && (
                  <div className="flex items-center gap-2 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3">
                    <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
                    <p className="text-sm text-destructive">{loginError}</p>
                  </div>
                )}
                <Button 
                  type="submit" 
                  className="w-full h-12 text-lg font-bold bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 rounded-xl mt-4"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter className="flex justify-center border-t border-white/5 pt-6 mt-2">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{" "}
              <Link href="/register"><span className="text-primary hover:underline font-semibold cursor-pointer">Sign up</span></Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </PublicLayout>
  );
}
